import { Camera, Flame, Lock, Package, Radio, Wifi } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { productImageUrl } from '@/lib/api';

interface ProductImageProps {
  imagen?: string | null;
  alt: string;
  modelo?: string;
  categoria?: string | null;
  fallbackLabel?: string;
  className?: string;
  imgClassName?: string;
  /** Si true, carga inmediato (detalle / above-the-fold). */
  priority?: boolean;
  /** contain = ficha producto (default); cover = banners/hero. */
  fit?: 'contain' | 'cover';
}

const categoriaIcon: Record<string, typeof Package> = {
  cctv: Camera,
  intrusion: Radio,
  acceso: Lock,
  incendio: Flame,
  redes: Wifi,
  kit: Package,
};

function PlaceholderVisual({
  categoria,
  label,
  className,
}: {
  categoria?: string | null;
  label: string;
  className?: string;
}) {
  const key = (categoria ?? 'kit').toLowerCase();
  const Icon = categoriaIcon[key] ?? Package;
  const isKit = key === 'kit' || !categoriaIcon[key];

  return (
    <div
      className={cn(
        'flex h-full w-full flex-col items-center justify-center gap-3 px-4 text-center',
        isKit
          ? 'bg-gradient-to-br from-brand-50 via-white to-surface-100'
          : 'bg-surface-100',
        className,
      )}
    >
      <div
        className={cn(
          'flex h-14 w-14 items-center justify-center rounded-2xl sm:h-16 sm:w-16',
          isKit ? 'bg-brand-100 text-brand-700' : 'bg-white text-surface-400 shadow-sm',
        )}
      >
        <Icon className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={1.5} />
      </div>
      <p className="line-clamp-3 max-w-[220px] text-sm font-medium leading-snug text-surface-700">
        {label}
      </p>
    </div>
  );
}

export function ProductImage({
  imagen,
  alt,
  modelo,
  categoria,
  fallbackLabel,
  className,
  imgClassName,
  priority = false,
  fit = 'contain',
}: ProductImageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(priority);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  const src = imagen ? productImageUrl(imagen) : null;
  const placeholderText = fallbackLabel?.trim() || alt?.trim() || modelo?.trim() || 'Producto';

  useEffect(() => {
    setFailed(false);
    setLoaded(false);
  }, [src]);

  useEffect(() => {
    if (priority || !src || inView) return;
    const el = containerRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '200px 0px', threshold: 0.01 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [priority, src, inView]);

  if (!src || failed) {
    return (
      <PlaceholderVisual
        categoria={categoria}
        label={placeholderText}
        className={className}
      />
    );
  }

  const shouldLoad = priority || inView;

  return (
    <div
      ref={containerRef}
      className={cn('relative h-full w-full overflow-hidden bg-surface-50', className)}
    >
      {!loaded && (
        <div
          className="absolute inset-0 animate-pulse bg-surface-100"
          aria-hidden
        />
      )}
      {shouldLoad && (
        <img
          src={src}
          alt={alt}
          loading={priority ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority ? 'high' : 'low'}
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={cn(
            'h-full w-full transition-opacity duration-200',
            fit === 'contain' ? 'object-contain p-2' : 'object-cover',
            loaded ? 'opacity-100' : 'opacity-0',
            imgClassName,
          )}
        />
      )}
    </div>
  );
}
